import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  getSmoothStepPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import api from '../api';

// ─── Function → colour mapping ────────────────────────────────────────────────

const FUNCTION_COLORS = {
  'Sales':             { bg: 'rgba(249,115,22,0.18)',  text: '#fb923c' },
  'Presales':          { bg: 'rgba(251,146,60,0.18)',  text: '#fca67a' },
  'Customer Success':  { bg: 'rgba(16,185,129,0.18)',  text: '#34d399' },
  'GTM':               { bg: 'rgba(139,92,246,0.18)',  text: '#a78bfa' },
  'Delivery':          { bg: 'rgba(59,130,246,0.18)',  text: '#60a5fa' },
  'Engineering':       { bg: 'rgba(6,182,212,0.18)',   text: '#22d3ee' },
  'Product':           { bg: 'rgba(168,85,247,0.18)',  text: '#c084fc' },
  'Marketing':         { bg: 'rgba(236,72,153,0.18)',  text: '#f472b6' },
  'Operations':        { bg: 'rgba(245,158,11,0.18)',  text: '#fbbf24' },
  'Finance':           { bg: 'rgba(132,204,22,0.18)',  text: '#a3e635' },
  'HR':                { bg: 'rgba(244,63,94,0.18)',   text: '#fb7185' },
  'Legal':             { bg: 'rgba(107,114,128,0.18)', text: '#9ca3af' },
  'Strategy':          { bg: 'rgba(217,119,6,0.18)',   text: '#fcd34d' },
  'Consulting':        { bg: 'rgba(8,145,178,0.18)',   text: '#38bdf8' },
  'Research':          { bg: 'rgba(124,58,237,0.18)',  text: '#c4b5fd' },
};

function getFunctionColor(fn) {
  return FUNCTION_COLORS[fn] || { bg: 'rgba(99,102,241,0.18)', text: '#a5b4fc' };
}

// ─── Company logo helpers ─────────────────────────────────────────────────────

function getCompanyDomain(company) {
  return company
    .toLowerCase()
    .replace(/[&,\.]/g, ' ')
    .replace(/\b(inc|corp|ltd|llc|co|group|holdings|pte|private|limited|bank|technologies|technology|solutions|services|international|global)\b/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim() + '.com';
}

function CompanyLogo({ company }) {
  const [failed, setFailed] = useState(false);
  const domain = getCompanyDomain(company || '');
  const initial = (company || '?')[0].toUpperCase();
  const logoUrl = `https://logo.clearbit.com/${domain}`;

  if (failed || !company) {
    return (
      <Box sx={{
        width: 28, height: 28, borderRadius: '6px', flexShrink: 0,
        bgcolor: 'rgba(99,102,241,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#a5b4fc' }}>
          {initial}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={logoUrl}
      alt={company}
      onError={() => setFailed(true)}
      sx={{ width: 28, height: 28, borderRadius: '6px', objectFit: 'contain', flexShrink: 0, bgcolor: '#fff', p: '2px' }}
    />
  );
}

// ─── Custom Node: Job ────────────────────────────────────────────────────────

function JobNode({ data }) {
  const fnColor = getFunctionColor(data.function);

  return (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        bgcolor: '#131929',
        border: '1px solid rgba(99,102,241,0.35)',
        borderRadius: '10px',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#6366f1', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: '#6366f1', width: 10, height: 10 }} />

      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
          <CompanyLogo company={data.company} />
          <Typography sx={{ fontSize: '0.72rem', color: '#a5b4fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.2 }}>
            {data.company || '—'}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--tx-hi)', lineHeight: 1.3 }}>
          {data.title || '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-lo)', mt: 0.5 }}>
          {data.from} → {data.to}
          {data.tenure && <span style={{ color: 'var(--tx-dim)', marginLeft: 6 }}>· {data.tenure}</span>}
        </Typography>
        {data.location && (
          <Stack direction="row" alignItems="center" gap={0.4} mt={0.4}>
            <LocationOnIcon sx={{ fontSize: 11, color: 'var(--tx-ft)' }} />
            <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-ft)' }}>{data.location}</Typography>
          </Stack>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Badges */}
      <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {data.function && (
          <Chip
            label={data.function}
            size="small"
            sx={{ bgcolor: fnColor.bg, color: fnColor.text, fontSize: '0.65rem', fontWeight: 600, height: 20 }}
          />
        )}
        {data.role_type && (
          <Chip
            label={data.role_type}
            size="small"
            sx={{
              bgcolor: data.role_type === 'Manager' ? 'rgba(249,115,22,0.18)' : 'rgba(99,102,241,0.12)',
              color: data.role_type === 'Manager' ? '#fb923c' : '#818cf8',
              fontSize: '0.65rem',
              fontWeight: 600,
              height: 20,
            }}
          />
        )}
      </Box>

      {/* Responsibilities */}
      {data.responsibilities && data.responsibilities.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
              Responsibilities
            </Typography>
            {data.responsibilities.slice(0, 4).map((r, i) => (
              <Typography key={i} sx={{ fontSize: '0.7rem', color: 'var(--tx-lo)', lineHeight: 1.5, display: 'flex', gap: 0.5 }}>
                <span style={{ color: fnColor.text, flexShrink: 0 }}>·</span>
                {r.length > 100 ? r.slice(0, 100) + '…' : r}
              </Typography>
            ))}
            {data.responsibilities.length > 4 && (
              <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-ft)', mt: 0.5 }}>
                +{data.responsibilities.length - 4} more
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}

// ─── Custom Node: Career Break ───────────────────────────────────────────────

function CareerBreakNode({ data }) {
  return (
    <Paper
      elevation={0}
      sx={{
        width: 200,
        bgcolor: 'rgba(99,102,241,0.06)',
        border: '1.5px dashed rgba(99,102,241,0.35)',
        borderRadius: '10px',
        p: 2,
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#6366f1', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: '#6366f1', width: 10, height: 10 }} />

      <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
        <PauseCircleOutlineIcon sx={{ fontSize: 14, color: '#818cf8' }} />
        <Typography sx={{ fontSize: '0.72rem', color: '#818cf8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Career Break
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-lo)' }}>
        {data.from} → {data.to}
      </Typography>
      {data.tenure && (
        <Typography sx={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 600, mt: 0.25 }}>
          {data.tenure}
        </Typography>
      )}
      {data.notes && (
        <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-dim)', mt: 1, fontStyle: 'italic' }}>
          {data.notes}
        </Typography>
      )}
    </Paper>
  );
}

// ─── Company-change edge: vertical divider line (horizontal flow) ─────────────

function CompanyChangeEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition }) {
  const [edgePath] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  return (
    <>
      <path id={id} className="react-flow__edge-path" d={edgePath} style={{ stroke: '#f97316', strokeWidth: 1.5, strokeDasharray: '6 4', fill: 'none' }} />
      <line x1={midX} y1={midY - 148} x2={midX} y2={midY + 148} stroke="#f97316" strokeWidth={1} strokeOpacity={0.75} />
    </>
  );
}

const NODE_TYPES = { job: JobNode, career_break: CareerBreakNode };
const EDGE_TYPES = { company_change: CompanyChangeEdge };

// ─── Main View Page ───────────────────────────────────────────────────────────

export default function CareerMapperViewPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkedinText, setLinkedinText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsePreview, setParsePreview] = useState(null); // { nodes, edges, node_count }
  const [parseError, setParseError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [profileId]);

  async function fetchProfile() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/career/profiles/${profileId}`);
      const p = res.data.profile;
      setProfile(p);
      // Select latest version by default
      if (p.versions.length > 0) {
        const latest = p.versions[p.versions.length - 1];
        setSelectedVersionId(latest.version_id);
        loadVersion(p.versions, latest.version_id);
      }
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  function loadVersion(versions, versionId) {
    const v = versions.find(v => v.version_id === versionId);
    if (!v) return;

    // Parse "MM/YYYY" or "Present" to total months
    const parseMs = str => {
      if (!str || str === 'Present') { const d = new Date(); return d.getFullYear() * 12 + d.getMonth() + 1; }
      const [m, y] = str.split('/');
      return parseInt(y, 10) * 12 + parseInt(m, 10);
    };

    // Time-proportional x positions (oldest left, most recent right)
    const X_START   = 100;
    const CENTER_Y  = 200;
    const JOB_H     = 260;
    const BREAK_H   = 130;
    const PX_PER_MO = 12;   // 12px per month = 144px per year
    const MIN_SPACE = 340;  // minimum x-gap between node starts (node width + breathing room)

    const fromMonths = v.nodes.map(n => parseMs(n.data?.from)).filter(m => m > 0);
    const minMonth = fromMonths.length > 0 ? Math.min(...fromMonths) : 0;

    let lastX = X_START - MIN_SPACE;
    const positionedNodes = v.nodes.map(n => {
      const fromMonth = parseMs(n.data?.from);
      const idealX = X_START + (fromMonth - minMonth) * PX_PER_MO;
      const x = Math.max(idealX, lastX + MIN_SPACE);
      lastX = x;
      const h = n.type === 'career_break' ? BREAK_H : JOB_H;
      return { ...n, position: { x, y: CENTER_Y - h / 2 }, draggable: false };
    });

    const nodeMap = Object.fromEntries(positionedNodes.map(n => [n.id, n]));
    const styledEdges = v.edges.map(edge => {
      const src = nodeMap[edge.source];
      const tgt = nodeMap[edge.target];
      const isCoChange = src?.type === 'job' && tgt?.type === 'job' && src.data?.company !== tgt.data?.company;
      return {
        ...edge,
        type: isCoChange ? 'company_change' : 'smoothstep',
        style: { stroke: isCoChange ? '#f97316' : '#6366f1', strokeWidth: 2 },
      };
    });

    setNodes(positionedNodes);
    setEdges(styledEdges);
  }

  function handleVersionChange(e) {
    const id = e.target.value;
    setSelectedVersionId(id);
    loadVersion(profile.versions, id);
  }

  async function handleParsePreview() {
    if (!linkedinText.trim()) return;
    setParsing(true);
    setParseError('');
    setParsePreview(null);
    try {
      const res = await api.post('/api/career/parse', { linkedin_text: linkedinText });
      if (!res.data.success) throw new Error(res.data.error);
      setParsePreview(res.data);
    } catch (err) {
      setParseError(err.response?.data?.error || err.message || 'Parse failed');
    } finally {
      setParsing(false);
    }
  }

  async function handleSaveVersion() {
    if (!parsePreview) return;
    setParsing(true);
    try {
      await api.post(`/api/career/profiles/${profileId}/versions`, { linkedin_text: linkedinText });
      setUploadOpen(false);
      setLinkedinText('');
      setParsePreview(null);
      fetchProfile();
    } catch (err) {
      setParseError(err.response?.data?.error || 'Failed to save version');
    } finally {
      setParsing(false);
    }
  }

  function handleCloseUpload() {
    setUploadOpen(false);
    setLinkedinText('');
    setParsePreview(null);
    setParseError('');
  }

  const currentVersion = profile?.versions.find(v => v.version_id === selectedVersionId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/career')} sx={{ mt: 2 }}>Back to Profiles</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column', bgcolor: '#0d1117' }}>
      {/* Toolbar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: 'var(--bg-surf)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <IconButton size="small" onClick={() => navigate('/career')} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Typography variant="subtitle1" fontWeight={700} color="var(--tx-hi)" sx={{ flexShrink: 0 }}>
          {profile?.person_name}
        </Typography>

        {profile?.versions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedVersionId}
              onChange={handleVersionChange}
              sx={{
                color: 'var(--tx-md)',
                fontSize: '0.82rem',
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.5)' },
              }}
            >
              {[...profile.versions].reverse().map(v => (
                <MenuItem key={v.version_id} value={v.version_id} sx={{ fontSize: '0.82rem' }}>
                  {v.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {currentVersion && (
          <Typography variant="caption" color="rgba(255,255,255,0.35)">
            {currentVersion.nodes.length} nodes
          </Typography>
        )}

        <Box flex={1} />

        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
          size="small"
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, fontWeight: 600, fontSize: '0.8rem' }}
        >
          Upload New Version
        </Button>
      </Box>

      {/* Graph area */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {profile?.versions.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Typography color="rgba(255,255,255,0.3)" variant="h6">
              No data yet
            </Typography>
            <Typography color="rgba(255,255,255,0.2)" variant="body2">
              Upload LinkedIn profile text to generate the career graph.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{ borderColor: 'rgba(99,102,241,0.5)', color: '#818cf8', borderRadius: 2 }}
            >
              Upload LinkedIn Data
            </Button>
          </Box>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.25 }}
            minZoom={0.15}
            maxZoom={1.5}
            panOnScroll
            defaultEdgeOptions={{ type: 'smoothstep', style: { stroke: '#6366f1', strokeWidth: 2 } }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="rgba(255,255,255,0.04)" gap={24} />
            <Controls
              style={{ background: '#131929', border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <MiniMap
              style={{ background: '#131929', border: '1px solid rgba(255,255,255,0.1)' }}
              nodeColor={n => n.type === 'career_break' ? 'rgba(99,102,241,0.3)' : '#6366f1'}
              maskColor="rgba(0,0,0,0.6)"
            />
          </ReactFlow>
        )}
      </Box>

      {/* Upload / Parse dialog */}
      <Dialog open={uploadOpen} onClose={handleCloseUpload} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>Upload New LinkedIn Version</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            On LinkedIn, go to your profile, press <strong>Ctrl+A</strong> (or Cmd+A), then <strong>Ctrl+C</strong> to copy everything. Paste it below.
          </Typography>

          <TextField
            multiline
            fullWidth
            rows={10}
            placeholder="Paste your LinkedIn profile text here…"
            value={linkedinText}
            onChange={e => setLinkedinText(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            inputProps={{ style: { fontSize: '0.8rem', lineHeight: 1.5 } }}
          />

          {parseError && (
            <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>
          )}

          {parsePreview && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Parsed successfully — <strong>{parsePreview.node_count} nodes</strong> detected
              ({parsePreview.nodes.filter(n => n.type === 'job').length} jobs,&nbsp;
              {parsePreview.nodes.filter(n => n.type === 'career_break').length} breaks).
              Click <strong>Save Version</strong> to add this to the profile.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseUpload} color="inherit">Cancel</Button>
          {!parsePreview ? (
            <Button
              onClick={handleParsePreview}
              disabled={!linkedinText.trim() || parsing}
              variant="outlined"
              sx={{ borderColor: '#6366f1', color: '#6366f1', borderRadius: 2 }}
            >
              {parsing ? <><CircularProgress size={16} sx={{ mr: 1, color: '#6366f1' }} />Parsing…</> : 'Parse & Preview'}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => { setParsePreview(null); setParseError(''); }}
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 2 }}
              >
                Re-parse
              </Button>
              <Button
                onClick={handleSaveVersion}
                disabled={parsing}
                variant="contained"
                sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, fontWeight: 600 }}
              >
                {parsing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Version'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
